import { useCallback, useMemo, useRef } from 'react';
import { useOverlayTriggerState } from 'react-stately';

import { Popup, PopupPlacement } from '../Popup';
import { TooltipContainerContext } from '../Tooltip/Tooltip';

import { MenuListContext } from './utils';
import { SubmenuContext } from './SubmenuTrigger/utils';
import { useMenuRoot } from './useMenuRoot';
import { MenuTriggerProps } from './types';

const PLACEMENT: PopupPlacement[] = ['right-start', 'left-start', 'left-end', 'right-end'];

export default function MenuTrigger(props: MenuTriggerProps) {
    const {
        trigger,
        children,
        placement,
        popupWidth,
        isOpen,
        defaultOpen,
        onOpenChange,
        closeAfterMouseLeaveTimeout,
        shouldCloseOnInteractOutside,
    } = props;
    const {
        isOpen: isMenuOpen,
        toggle,
        close,
    } = useOverlayTriggerState({
        isOpen,
        defaultOpen,
        onOpenChange,
    });
    const { triggerRef, popupRef, contextValue } = useMenuRoot({ close, shouldCloseOnInteractOutside });

    const onPress = useCallback(() => {
        toggle();
    }, [toggle]);

    /** Дополняем базовую логику дополнительной функцией-предикатом */
    const handleShouldCloseOnInteractOutside = useCallback(
        (element: HTMLElement) => {
            if (triggerRef.current?.contains(element)) {
                return false;
            }

            return shouldCloseOnInteractOutside?.(element) ?? true;
        },
        [triggerRef, shouldCloseOnInteractOutside],
    );

    const submenuContextValue = useMemo(
        () => ({
            parentMenuRef: popupRef,
        }),
        [popupRef],
    );

    const leaveTimer = useRef<ReturnType<typeof setTimeout>>();

    const handleMouseEnter = useCallback(() => {
        if (leaveTimer.current) {
            clearTimeout(leaveTimer.current);
        }
    }, [leaveTimer]);

    const handleMouseLeave = useCallback(() => {
        /** Если не указан, то ничего не делаем */
        if (closeAfterMouseLeaveTimeout === undefined) {
            return;
        }

        if (closeAfterMouseLeaveTimeout === 0) {
            return close();
        }

        leaveTimer.current = setTimeout(close, closeAfterMouseLeaveTimeout);
    }, [leaveTimer, closeAfterMouseLeaveTimeout, close]);

    return (
        <>
            {trigger({ onPress, onClick: onPress, ref: triggerRef }, { isOpen: isMenuOpen })}
            <Popup
                triggerRef={triggerRef}
                ref={popupRef}
                isOpen={isMenuOpen}
                onClose={close}
                placement={placement ?? PLACEMENT}
                matchTriggerWidth={popupWidth === 'trigger'}
                shouldCloseOnInteractOutside={handleShouldCloseOnInteractOutside}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <MenuListContext.Provider value={contextValue}>
                    <SubmenuContext.Provider value={submenuContextValue}>
                        <TooltipContainerContext.Provider value={popupRef}>{children}</TooltipContainerContext.Provider>
                    </SubmenuContext.Provider>
                </MenuListContext.Provider>
            </Popup>
        </>
    );
}
